import React, { useEffect, useState } from 'react'
import get from 'lodash/get'

import { toast } from '@fielded/shared-ui/src/components/Page'

import { formatDate } from '../../van-shared/utils/utils'
import { useAmplitudeAnalytics } from '../../common/analytics-tracking/AmplitudeAnalyticsProvider'
import { VIEWS, getAllBranches } from './utils'
import HomeStockReports from './HomeStockReports'

const DEFAULT_SELECTED_BRANCH = { branchName: '', branchId: '' }
const PAGE_LIMIT = 25

const HomeStockReportsContainer = ({ history, api, user, config }) => {
  const trackingHandler = useAmplitudeAnalytics()

  const [reports, setReports] = useState([])
  const [previousReports, setPreviousReports] = useState([])
  const [selectedBranch, setSelectedBranch] = useState(DEFAULT_SELECTED_BRANCH)
  const [loadingReports, setLoadingReports] = useState(false)
  const [loadingReport, setLoadingReport] = useState(false)
  const [downloadingReport, setDownloadingReport] = useState(false)
  const [downloadedReport, setDownloadedreport] = useState({})
  const [totalReportsNumber, setTotalReportsNumber] = useState(0)
  const [currentOffset, setCurrentOffset] = useState(0)

  const pathname = get(history, 'location.pathname')
  const view = get(history, 'location.state.view', 'all')
  const organisationId = get(user, 'organisationId')
  const branches = getAllBranches(config)

  const loadReports = async (offset = 0) => {
    setLoadingReports(true)
    try {
      const params = {
        organisationId,
        limit: PAGE_LIMIT,
        offset
      }

      if (selectedBranch.branchId && selectedBranch.branchId.trim()) {
        params.branchCode = selectedBranch.branchId
      }

      const { reports, total } = await api.rmsInternal.listStockEvents(params)
      setReports(reports)
      setTotalReportsNumber(total)
      return reports
    } catch (error) {
      console.warn(error)
      return []
    } finally {
      setLoadingReports(false)
    }
  }

  const initialize = async () => {
    if (!view || view === VIEWS.ALL) {
      setSelectedBranch(DEFAULT_SELECTED_BRANCH)
      setCurrentOffset(0)

      if (!reports.length && !previousReports.length) {
        const newReports = await loadReports(0)
        setPreviousReports(newReports)
      } else {
        setReports(previousReports)
      }
    } else {
      setReports([])
    }
  }

  const handleSelectBranch = (option) => {
    const branchName = get(option, 'label')
    const branchId = get(option, 'value')
    setReports([])
    setCurrentOffset(0)

    if (!branchName || !branchId) {
      setSelectedBranch(DEFAULT_SELECTED_BRANCH)
      return
    }
    setSelectedBranch({ branchName, branchId })
  }

  const onPageChange = async (newOffset) => {
    setCurrentOffset(newOffset)
    await loadReports(newOffset)
  }

  const handleGenerateReport = async () => {
    setLoadingReport(true)
    try {
      await loadReports(currentOffset)
    } catch (error) {
      console.warn(error)
    } finally {
      setLoadingReport(false)
    }
  }

  const downloadReport = (file, date) => {
    const url = URL.createObjectURL(file)
    const timestamp = new Date().toJSON()
    const selectedBranchName = get(selectedBranch, 'branchName')

    let filename = `stock-reports-${timestamp}.xlsx`
    let children = `Your stock status report for ${formatDate(date, 'printShort')} has been downloaded successfully.`
    if (selectedBranchName) {
      filename = `${selectedBranchName}-stock-report-${timestamp}.xlsx`
      children = `Stock status report for ${selectedBranchName} has been downloaded successfully`
    }

    // Create a temporary download link
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()

    // Cleanup
    URL.revokeObjectURL(url)
    document.body.removeChild(a)

    toast({
      title: 'Report downloaded',
      type: 'success',
      children
    })
  }

  const handleDownloadReport = async (report) => {
    setDownloadingReport(true)

    try {
      setDownloadedreport(report)

      const date = report.createdAt
      const payload = { organisationId, date }
      if (view === VIEWS.SINGLE) {
        payload.branchCode = selectedBranch.branchId
      }
      const file = await api.rmsInternal.listWarehouseStock(payload)
      downloadReport(file, date)
      trackingHandler.report.downloaded({
        branchName: selectedBranch.branchName,
        createdAt: report.createdAt
      })
    } catch (error) {
      toast({
        title: error.message || 'Something went wrong while exporting. Please try again.',
        type: 'warning'
      })
    } finally {
      setDownloadingReport(false)
    }
  }

  useEffect(() => {
    initialize()
  }, [view])

  return (
    <HomeStockReports
      view={view}
      pathname={pathname}
      reports={reports}
      branches={branches}
      selectedBranch={selectedBranch}
      loadingReports={loadingReports}
      loadingReport={loadingReport}
      downloadingReport={downloadingReport}
      downloadedReport={downloadedReport}
      onSelectBranch={handleSelectBranch}
      onGenerateReport={handleGenerateReport}
      onDownloadReport={handleDownloadReport}
      totalReportsNumber={totalReportsNumber}
      currentOffset={currentOffset}
      onPageChange={onPageChange}
      pageLimit={PAGE_LIMIT}
    />
  )
}

export default HomeStockReportsContainer
